import { useState } from "react";
import { gold, w } from "../constants";
import { Card } from "../components";
import { initPdf, downloadExcel } from "./exportHelpers";
function DownloadModal({ onPdf, onExcel, onClose }) {
  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000 }}>
      <div style={{ background:"#fff",borderRadius:16,padding:28,width:"100%",maxWidth:360,margin:"0 16px" }}>
        <h2 style={{ fontSize:17,fontWeight:500,margin:"0 0 6px",color:"#1a1a1a" }}>Download your results</h2>
        <p style={{ fontSize:13,color:"#555",margin:"0 0 20px" }}>Choose your preferred format:</p>
        <button onClick={onPdf} style={{ width:"100%",padding:"12px",borderRadius:10,border:"none",background:"#A0845C",color:"#fff",fontSize:14,fontWeight:500,cursor:"pointer",marginBottom:10 }}>📄 Download PDF</button>
        <button onClick={onExcel} style={{ width:"100%",padding:"12px",borderRadius:10,border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",color:"#1a1a1a",fontSize:14,cursor:"pointer",marginBottom:10 }}>📊 Download Excel</button>
        <button onClick={onClose} style={{ width:"100%",padding:"10px",borderRadius:10,border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",color:"#555",fontSize:13,cursor:"pointer" }}>Cancel</button>
      </div>
    </div>
  );
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CATEGORIES = {
  income: ["Salary","Business","Side income","Other income"],
  giving: ["Tithe","Offerings","Charity","Other giving"],
  saving: ["Emergency fund","Goal saving","Investments","Other saving"],
  spending: ["Housing","Food","Transport","Utilities","Healthcare","Education","Personal","Entertainment","Other"],
};
const CATEGORY_COLORS = { income:"#057A55", giving:"#1A56DB", saving:"#7E3AF2", spending:"#E02424" };
const CATEGORY_LABELS = { income:"Income", giving:"Giving", saving:"Saving", spending:"Spending" };

const monthKey = (year, month) => `${year}-${String(month+1).padStart(2,"0")}`;

function getTotal(monthData, section, field) {
  if(!monthData?.[section]) return 0;
  return Object.values(monthData[section]).reduce((s,v)=>s+(parseFloat(v[field])||0),0);
}

function exportBudgetPdf(year, budgetData, userName) {
  const p = initPdf();
  p.addHeader(`Personal Budget — ${year}`, userName);
  MONTHS.forEach((month, mi) => {
    const k = monthKey(year, mi);
    const md = budgetData[k];
    const hasData = md && Object.values(md).some(s=>Object.values(s).some(v=>v.planned||v.actual));
    if(!hasData) return;
    p.heading2(`${month} ${year}`);
    Object.keys(CATEGORIES).forEach(section => {
      const planned = getTotal(md, section, "planned");
      const actual = getTotal(md, section, "actual");
      if(!planned && !actual) return;
      p.heading3(CATEGORY_LABELS[section]);
      p.body(`Planned: ₹${planned.toLocaleString()}  |  Actual: ₹${actual.toLocaleString()}  |  Diff: ${(actual-planned)>=0?"+":""}₹${Math.abs(actual-planned).toLocaleString()}`, 4);
    });
    p.space(4);
  });
  p.addFooters();
  p.doc.save(`budget-${year}-${new Date().toISOString().slice(0,10)}.pdf`);
}

function exportBudgetExcel(year, budgetData, userName) {
  const sheets = [];
  const annualData = [
    ["My Self-Management Framework — Personal Budget"],
    [`Name: ${userName}`, `Year: ${year}`, `Exported: ${new Date().toLocaleDateString("en-IN")}`],
    [],
    ["Month","Income Planned","Income Actual","Giving Planned","Giving Actual","Saving Planned","Saving Actual","Spending Planned","Spending Actual"],
  ];
  MONTHS.forEach((month, mi) => {
    const k = monthKey(year, mi);
    const md = budgetData[k];
    annualData.push([month, getTotal(md,"income","planned"), getTotal(md,"income","actual"), getTotal(md,"giving","planned"), getTotal(md,"giving","actual"), getTotal(md,"saving","planned"), getTotal(md,"saving","actual"), getTotal(md,"spending","planned"), getTotal(md,"spending","actual")]);
  });
  sheets.push({ name:"Annual Summary", data:annualData });
  MONTHS.forEach((month, mi) => {
    const k = monthKey(year, mi);
    const md = budgetData[k];
    if(!md) return;
    const sheetData = [[`${month} ${year}`],[`Name: ${userName}`],[],["Section","Category","Planned (₹)","Actual (₹)","Difference"]];
    Object.keys(CATEGORIES).forEach(section => {
      CATEGORIES[section].forEach(cat => {
        const r = md[section]?.[cat];
        if(!r?.planned && !r?.actual) return;
        const diff = (parseFloat(r?.actual)||0)-(parseFloat(r?.planned)||0);
        sheetData.push([CATEGORY_LABELS[section], cat, r?.planned||0, r?.actual||0, diff.toFixed(0)]);
      });
    });
    sheets.push({ name:month, data:sheetData });
  });
  downloadExcel(sheets, `budget-${year}-${new Date().toISOString().slice(0,10)}.xlsx`);
}

function MonthView({ year, month, data, onChange, onBack }) {
  const [section, setSection] = useState("income");
  const md = data || {};
  const color = CATEGORY_COLORS[section];

  const upd = (cat, field, val) => {
    const d = JSON.parse(JSON.stringify(md));
    if(!d[section]) d[section] = {};
    if(!d[section][cat]) d[section][cat] = {planned:"",actual:""};
    d[section][cat][field] = val;
    onChange(d);
  };

  const totalPlanned = getTotal(md, section, "planned");
  const totalActual = getTotal(md, section, "actual");
  const diff = totalActual - totalPlanned;

  return (
    <div>
      <button onClick={onBack} style={{ fontSize:13,color:w.text2,background:"transparent",border:"none",cursor:"pointer",marginBottom:16,padding:0 }}>← Back</button>
      <h2 style={{ fontSize:18,fontWeight:500,margin:"0 0 4px",color:w.text }}>{MONTHS[month]} {year}</h2>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:20 }}>
        {Object.keys(CATEGORIES).map(s=>{
          const t=getTotal(md,s,"actual")||getTotal(md,s,"planned");
          return <div key={s} onClick={()=>setSection(s)} style={{ background:section===s?CATEGORY_COLORS[s]:w.bg,border:`0.5px solid ${section===s?CATEGORY_COLORS[s]:w.border}`,borderRadius:10,padding:"10px 12px",cursor:"pointer" }}>
            <p style={{ fontSize:11,color:section===s?"#fff":w.text3,margin:"0 0 4px" }}>{CATEGORY_LABELS[s]}</p>
            <p style={{ fontSize:14,fontWeight:500,color:section===s?"#fff":w.text,margin:0 }}>₹{t.toLocaleString()}</p>
          </div>;
        })}
      </div>
      <div style={{ display:"flex",gap:12,marginBottom:16 }}>
        <div style={{ flex:1,background:w.bg2,borderRadius:8,padding:"10px 14px" }}><p style={{ fontSize:11,color:w.text3,margin:"0 0 2px" }}>Planned</p><p style={{ fontSize:16,fontWeight:500,color:w.text,margin:0 }}>₹{totalPlanned.toLocaleString()}</p></div>
        <div style={{ flex:1,background:w.bg2,borderRadius:8,padding:"10px 14px" }}><p style={{ fontSize:11,color:w.text3,margin:"0 0 2px" }}>Actual</p><p style={{ fontSize:16,fontWeight:500,color:w.text,margin:0 }}>₹{totalActual.toLocaleString()}</p></div>
        <div style={{ flex:1,background:w.bg2,borderRadius:8,padding:"10px 14px" }}><p style={{ fontSize:11,color:w.text3,margin:"0 0 2px" }}>Difference</p><p style={{ fontSize:16,fontWeight:500,color:diff>=0?"#057A55":"#E02424",margin:0 }}>{diff>=0?"+":""}₹{Math.abs(diff).toLocaleString()}</p></div>
      </div>
      <Card>
        <h3 style={{ fontSize:14,fontWeight:500,color,margin:"0 0 14px" }}>{CATEGORY_LABELS[section]}</h3>
        <div style={{ display:"flex",gap:8,marginBottom:8 }}>
          <span style={{ flex:2,fontSize:12,color:w.text3 }}>Category</span>
          <span style={{ flex:1,fontSize:12,color:w.text3,textAlign:"right" }}>Planned (₹)</span>
          <span style={{ flex:1,fontSize:12,color:w.text3,textAlign:"right" }}>Actual (₹)</span>
        </div>
        {CATEGORIES[section].map(cat=>(
          <div key={cat} style={{ display:"flex",gap:8,alignItems:"center",marginBottom:10 }}>
            <span style={{ flex:2,fontSize:13,color:w.text }}>{cat}</span>
            <input value={md[section]?.[cat]?.planned||""} onChange={e=>upd(cat,"planned",e.target.value)} placeholder="0" style={{ flex:1,border:`0.5px solid ${w.border}`,borderRadius:6,padding:"5px 8px",fontSize:13,textAlign:"right",background:w.bg2,color:w.text }} />
            <input value={md[section]?.[cat]?.actual||""} onChange={e=>upd(cat,"actual",e.target.value)} placeholder="0" style={{ flex:1,border:`0.5px solid ${w.border}`,borderRadius:6,padding:"5px 8px",fontSize:13,textAlign:"right",background:w.bg2,color:w.text }} />
          </div>
        ))}
        <div style={{ display:"flex",gap:8,paddingTop:10,borderTop:`0.5px solid ${w.border}` }}>
          <span style={{ flex:2,fontSize:13,fontWeight:500,color:w.text }}>Total</span>
          <span style={{ flex:1,fontSize:13,fontWeight:500,color:w.text,textAlign:"right" }}>₹{totalPlanned.toLocaleString()}</span>
          <span style={{ flex:1,fontSize:13,fontWeight:500,color:w.text,textAlign:"right" }}>₹{totalActual.toLocaleString()}</span>
        </div>
      </Card>
    </div>
  );
}

function AnnualView({ year, budgetData, onBack }) {
  const [section, setSection] = useState("income");
  const color = CATEGORY_COLORS[section];
  const monthTotals = MONTHS.map((_,mi)=>{ const k=monthKey(year,mi);const md=budgetData[k];return { planned:getTotal(md,section,"planned"), actual:getTotal(md,section,"actual") }; });
  const annualPlanned = monthTotals.reduce((s,m)=>s+m.planned,0);
  const annualActual = monthTotals.reduce((s,m)=>s+m.actual,0);
  const maxVal = Math.max(...monthTotals.map(m=>Math.max(m.planned,m.actual)),1);

  return (
    <div>
      <button onClick={onBack} style={{ fontSize:13,color:w.text2,background:"transparent",border:"none",cursor:"pointer",marginBottom:16,padding:0 }}>← Back</button>
      <h2 style={{ fontSize:18,fontWeight:500,margin:"0 0 16px",color:w.text }}>Annual Summary — {year}</h2>
      <div style={{ display:"flex",gap:8,marginBottom:20,flexWrap:"wrap" }}>
        {Object.keys(CATEGORIES).map(s=><button key={s} onClick={()=>setSection(s)} style={{ padding:"6px 14px",borderRadius:8,fontSize:13,cursor:"pointer",fontWeight:section===s?500:400,background:section===s?CATEGORY_COLORS[s]:"transparent",color:section===s?"#fff":w.text2,border:`0.5px solid ${section===s?CATEGORY_COLORS[s]:w.border}` }}>{CATEGORY_LABELS[s]}</button>)}
      </div>
      <div style={{ display:"flex",gap:12,marginBottom:20 }}>
        <div style={{ flex:1,background:w.bg2,borderRadius:8,padding:"12px 16px" }}><p style={{ fontSize:12,color:w.text3,margin:"0 0 4px" }}>Annual planned</p><p style={{ fontSize:20,fontWeight:500,color:w.text,margin:0 }}>₹{annualPlanned.toLocaleString()}</p></div>
        <div style={{ flex:1,background:w.bg2,borderRadius:8,padding:"12px 16px" }}><p style={{ fontSize:12,color:w.text3,margin:"0 0 4px" }}>Annual actual</p><p style={{ fontSize:20,fontWeight:500,color:w.text,margin:0 }}>₹{annualActual.toLocaleString()}</p></div>
      </div>
      <Card>
        <h3 style={{ fontSize:14,fontWeight:500,color:w.text,margin:"0 0 16px" }}>Monthly breakdown</h3>
        <div style={{ display:"flex",gap:4,alignItems:"flex-end",height:140,marginBottom:8 }}>
          {monthTotals.map((m,i)=>(
            <div key={i} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2 }}>
              <div style={{ width:"100%",display:"flex",gap:1,alignItems:"flex-end",height:120 }}>
                <div style={{ flex:1,background:color+"40",borderRadius:"3px 3px 0 0",height:`${(m.planned/maxVal)*100}%`,minHeight:m.planned?2:0 }} />
                <div style={{ flex:1,background:color,borderRadius:"3px 3px 0 0",height:`${(m.actual/maxVal)*100}%`,minHeight:m.actual?2:0 }} />
              </div>
              <span style={{ fontSize:9,color:w.text3 }}>{MONTHS[i]}</span>
            </div>
          ))}
        </div>
        <div style={{ display:"flex",gap:16,fontSize:11,color:w.text3 }}>
          <span style={{ display:"flex",alignItems:"center",gap:4 }}><span style={{ width:10,height:10,background:color+"40",borderRadius:2,display:"inline-block" }} />Planned</span>
          <span style={{ display:"flex",alignItems:"center",gap:4 }}><span style={{ width:10,height:10,background:color,borderRadius:2,display:"inline-block" }} />Actual</span>
        </div>
      </Card>
      <Card>
        <h3 style={{ fontSize:14,fontWeight:500,color:w.text,margin:"0 0 12px" }}>Month by month</h3>
        <table style={{ width:"100%",fontSize:13,borderCollapse:"collapse" }}>
          <thead><tr style={{ color:w.text3 }}><th style={{ textAlign:"left",paddingBottom:8,fontWeight:400 }}>Month</th><th style={{ textAlign:"right",paddingBottom:8,fontWeight:400 }}>Planned</th><th style={{ textAlign:"right",paddingBottom:8,fontWeight:400 }}>Actual</th><th style={{ textAlign:"right",paddingBottom:8,fontWeight:400 }}>Diff</th></tr></thead>
          <tbody>
            {monthTotals.map((m,i)=>{ const d=m.actual-m.planned; return <tr key={i} style={{ borderTop:`0.5px solid ${w.border}` }}>
              <td style={{ padding:"8px 0",color:w.text }}>{MONTHS[i]}</td>
              <td style={{ padding:"8px 0",textAlign:"right",color:w.text2 }}>₹{m.planned.toLocaleString()}</td>
              <td style={{ padding:"8px 0",textAlign:"right",color:w.text2 }}>₹{m.actual.toLocaleString()}</td>
              <td style={{ padding:"8px 0",textAlign:"right",color:d>=0?"#057A55":"#E02424",fontWeight:500 }}>{m.planned||m.actual?(d>=0?"+":"")+d.toLocaleString():"—"}</td>
            </tr>; })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function BudgetModule({ moduleData, onSave, onBack, userName }) {
  const now = new Date();
  const [view, setView] = useState("home");
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [showDownload, setShowDownload] = useState(false);
  const budgetData = moduleData?.budgetData || {};

  const saveMonth = (year, month, data) => {
    const k = monthKey(year, month);
    onSave({ budgetData:{ ...budgetData, [k]:data } });
  };

  if(view==="month") return <MonthView year={selectedYear} month={selectedMonth} data={budgetData[monthKey(selectedYear,selectedMonth)]} onChange={d=>saveMonth(selectedYear,selectedMonth,d)} onBack={()=>setView("home")} />;
  if(view==="annual") return <AnnualView year={selectedYear} budgetData={budgetData} onBack={()=>setView("home")} />;

  const years = [...new Set([now.getFullYear()-1, now.getFullYear(), now.getFullYear()+1])];

  return (
    <div>
      {showDownload&&<DownloadModal onPdf={()=>{exportBudgetPdf(selectedYear,budgetData,userName||"User");setShowDownload(false);}} onExcel={()=>{exportBudgetExcel(selectedYear,budgetData,userName||"User");setShowDownload(false);}} onClose={()=>setShowDownload(false)} />}
      <button onClick={onBack} style={{ fontSize:13,color:w.text2,background:"transparent",border:"none",cursor:"pointer",marginBottom:16,padding:0 }}>← Back to Modules</button>
      <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:8 }}>
        <div style={{ width:40,height:40,borderRadius:10,background:`${gold}20`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20 }}>💰</div>
        <div><h2 style={{ fontSize:18,fontWeight:500,margin:0,color:w.text }}>Personal Budget</h2><p style={{ fontSize:13,color:w.text3,margin:0 }}>Monthly tracking · Annual rollup</p></div>
      </div>
      <p style={{ fontSize:13,color:gold,fontStyle:"italic",margin:"16px 0 20px" }}>Honor God with your wealth — give first, save second, live on the rest.</p>
      <div style={{ display:"flex",gap:8,marginBottom:20 }}>
        {years.map(y=><button key={y} onClick={()=>setSelectedYear(y)} style={{ padding:"6px 16px",borderRadius:8,fontSize:13,cursor:"pointer",fontWeight:selectedYear===y?500:400,background:selectedYear===y?gold:"transparent",color:selectedYear===y?"#fff":w.text2,border:`0.5px solid ${selectedYear===y?gold:w.border}` }}>{y}</button>)}
      </div>
      <button onClick={()=>setView("annual")} style={{ width:"100%",padding:"12px",borderRadius:10,border:`0.5px solid ${gold}`,background:"transparent",color:gold,fontSize:14,fontWeight:500,cursor:"pointer",marginBottom:10 }}>View Annual Summary — {selectedYear}</button>
      <button onClick={()=>setShowDownload(true)} style={{ width:"100%",padding:"12px",borderRadius:10,border:`0.5px solid ${w.border}`,background:"transparent",color:w.text2,fontSize:13,cursor:"pointer",marginBottom:16 }}>Download {selectedYear} Budget ↓</button>
      <h3 style={{ fontSize:14,fontWeight:500,color:w.text,margin:"0 0 12px" }}>Monthly budgets</h3>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8 }}>
        {MONTHS.map((m,mi)=>{
          const k=monthKey(selectedYear,mi);const md=budgetData[k];
          const hasData=md&&Object.values(md).some(s=>Object.values(s).some(v=>v.planned||v.actual));
          const income=getTotal(md,"income","actual")||getTotal(md,"income","planned");
          const spending=getTotal(md,"spending","actual")||getTotal(md,"spending","planned");
          const isCurrent=selectedYear===now.getFullYear()&&mi===now.getMonth();
          return (
            <button key={mi} onClick={()=>{setSelectedMonth(mi);setView("month");}} style={{ background:w.bg,border:`0.5px solid ${isCurrent?gold:w.border}`,borderRadius:10,padding:"12px",textAlign:"left",cursor:"pointer" }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=gold}
              onMouseLeave={e=>e.currentTarget.style.borderColor=isCurrent?gold:w.border}>
              <p style={{ fontSize:13,fontWeight:isCurrent?500:400,color:isCurrent?gold:w.text,margin:"0 0 6px" }}>{m}</p>
              {hasData?<><p style={{ fontSize:11,color:"#057A55",margin:"0 0 2px" }}>₹{income.toLocaleString()}</p><p style={{ fontSize:11,color:"#E02424",margin:0 }}>₹{spending.toLocaleString()}</p></>:<p style={{ fontSize:11,color:w.text3,margin:0 }}>Not started</p>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
